/**
 * Bug #758 — UI route gate for Connections write surfaces.
 *
 * The /connections/new-mcp, /connections/new-api, /connections/new-function
 * pages all require power_user+ (matches the backend's
 * requireConnectionsAdmin gate in routes/v1/connections-module.ts).
 * Pre-fix the routes rendered the full registration / authoring UI
 * for member + viewer tokens. Backend mutations still denied at 403,
 * but the UI/contract drift surfaced a confusing UX (#758) and is a
 * surface-area / permission-contract bug.
 *
 * B1-T3: it keys off `useHasPermission('write', 'connections')` — literally
 * the grant `requireConnectionsAdmin`'s replacement asks for
 * (`requirePermissionOr403(req, reply, 'write', 'connections')`), so the UI
 * gate and the API gate cannot drift and a tenant's own role is honoured.
 * It used to key off `hasRole('power_user')` against a browser-side ladder.
 * Sysadmin
 * (audience='argusai-admin') is intentionally NOT allowed here — the
 * sysadmin authoring surfaces live under /sysadmin/catalog/* (see
 * sysadmin/catalog.mcp.* etc.) and tenant-scoped registration would
 * always 401 with INVALID_AUDIENCE.
 */

import { ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth-context';
import { useHasPermission } from '@/lib/use-has-permission';
import { decideConnectionsRoleGate } from './-connections-role-gate.helpers';

interface ConnectionsRoleGateProps {
  /** Where to send the user when they tap "Back to Connections". */
  backHref: string;
  /**
   * Surface label for the placeholder ("MCP server", "API connection",
   * "function"). Used in the headline so the rejection is intelligible.
   */
  surface: string;
  /** Authoring UI to render when the role check passes. */
  children: ReactNode;
}

export function ConnectionsRoleGate({
  backHref,
  surface,
  children,
}: ConnectionsRoleGateProps) {
  const { contextKind } = useAuth();
  const canWriteConnections = useHasPermission('write', 'connections');
  const navigate = useNavigate();

  const outcome = decideConnectionsRoleGate({
    contextKind,
    canWriteConnections,
  });

  // Sysadmin context: the tenant-scoped Connections write API would
  // 401 INVALID_AUDIENCE anyway. Direct the operator to the sysadmin
  // catalog where the equivalent system-scoped authoring surface lives.
  if (outcome === 'deny_sysadmin_context') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Shield className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Use the sysadmin catalog</h2>
        <p className="text-sm text-muted-foreground max-w-md mt-2">
          Registering a {surface} from the sysadmin context is done under{' '}
          <span className="font-mono text-foreground">/sysadmin/catalog</span>.
          The tenant-scoped Connections surface only accepts tenant JWTs.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: backHref })}
        >
          Back
        </Button>
      </div>
    );
  }

  if (outcome === 'deny_role_too_low') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Shield className="h-12 w-12 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">
          Permission required
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mt-2">
          Registering a new {surface} needs the Connections write permission.
          Ask a workspace admin to grant it, on your role or a new one.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: backHref })}
        >
          Back to Connections
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
